import type { AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';
import { redactDeep, redactHeaders } from '@/lib/sentry/redact';
import {
    getHttpStatus,
    getHttpUrl,
    captureAsError,
    captureAsEvent,
    isCanceledError,
    isNetworkNoise,
} from './http';
import { isEnabled } from '@/lib/sentry/sentry';

function getHttpMethod(err: any): string | undefined {
    return err?.response?.config?.method ?? err?.config?.method;
}

function getParams(err: any): any {
    return err?.response?.config?.params ?? err?.config?.params;
}

function getHeaders(err: any): any {
    return err?.response?.config?.headers ?? err?.config?.headers;
}

function getResponseData(err: any): any {
    return err?.response?.data;
}

export function installSentryAxiosInterceptor(client: AxiosInstance, isExternal?: boolean) {
    client.interceptors.response.use(
        (response) => response,
        (err) => {
            const status = getHttpStatus(err);
            const url = getHttpUrl(err);
            const method = getHttpMethod(err);

            // Tag for global beforeSend filters
            err.__http = { status, url, method };

            // Don't do anything in dev/local unless you explicitly want it
            if (!isEnabled()) {
                return Promise.reject(err);
            }

            // 0) Canceled requests (e.g. TanStack Query aborts on navigation): not a failure
            if (isCanceledError(err)) {
                return Promise.reject(err);
            }

            // 1) Network noise: no issue, no log — pure transport noise
            if (isNetworkNoise(err)) {
                return Promise.reject(err);
            }

            // 2) 422, 5xx, or missing status: ERROR-level Sentry Issue
            if (captureAsError(status)) {
                Sentry.withScope((scope) => {
                    scope.setTag('type', 'http');
                    scope.setTag('http.status', String(status ?? 'unknown'));
                    if (url) scope.setTag('http.url', String(url));
                    if (method) scope.setTag('http.method', String(method));

                    scope.setFingerprint([
                        String(method ?? 'GET'),
                        String(url ?? 'unknown'),
                        String(status ?? 'unknown'),
                    ]);

                    scope.setContext('http', {
                        url,
                        method,
                        status,
                        params: redactDeep(getParams(err)),
                        response: redactDeep(getResponseData(err)),
                        headers: redactHeaders(getHeaders(err) ?? {}),
                    });
                    if (isExternal) {
                        Sentry.logger.warn('External HTTP Error', {
                            url,
                            method,
                            message: String(err?.message ?? ''),
                        });
                    } else {
                        Sentry.captureException(err);
                    }
                });
                return Promise.reject(err);
            }

            // 3) All other 4xx (400, 401, 403, 404, 409, 429, ...): lightweight Sentry Log,
            // visible as an event but never an ERROR-level Issue.
            if (captureAsEvent(status)) {
                Sentry.logger.warn('HTTP 4xx', {
                    url,
                    method,
                    status,
                    response: redactDeep(getResponseData(err)),
                });
            }

            return Promise.reject(err);
        },
    );
}
